import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { b2bGetTemplates, b2bGetTemplate, b2bDeleteTemplate } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';

// Type display config — color, label
const TYPE_META = {
  fear_forward:          { color: '#ef4444', label: 'Fear Forward'          },
  ai_forward:            { color: '#7c3aed', label: 'AI Forward'            },
  audit_compliance:      { color: '#0176CE', label: 'Audit / Compliance'    },
  operational_efficiency:{ color: '#f59e0b', label: 'Operational Efficiency'},
  social_proof:          { color: '#10b981', label: 'Social Proof'          },
  case_study:            { color: '#0891b2', label: 'Case Study'            },
  insight:               { color: '#6366f1', label: 'Insight'               },
};

function typeMeta(key) {
  return TYPE_META[key] || { color: '#94a3b8', label: key?.replace(/_/g, ' ') || 'General' };
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

function replaceVars(html) {
  return (html || '')
    .replace(/\{\{first_name\}\}/gi, 'Jane')
    .replace(/\{\{last_name\}\}/gi,  'Smith')
    .replace(/\{\{full_name\}\}/gi,  'Jane Smith')
    .replace(/\{\{company\}\}/gi,    'Acme Corp')
    .replace(/\{\{title\}\}/gi,      'VP of Operations');
}

export default function B2BMarketingTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [preview, setPreview]     = useState(null);   // full template object
  const [loadingPreview, setLoadingPreview] = useState(false);

  function load() {
    setLoading(true);
    b2bGetTemplates()
      .then(r => setTemplates(r.templates || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function openPreview(t) {
    setLoadingPreview(true);
    try {
      const full = await b2bGetTemplate(t.template_id);
      setPreview(full.template);
    } catch (_) {}
    finally { setLoadingPreview(false); }
  }

  async function deleteTemplate(t, e) {
    e.stopPropagation();
    if (!confirm(`Delete "${t.template_name}"?`)) return;
    await b2bDeleteTemplate(t.template_id);
    if (preview?.template_id === t.template_id) setPreview(null);
    load();
  }

  async function deleteFromPreview() {
    if (!confirm(`Delete "${preview.template_name}"?`)) return;
    await b2bDeleteTemplate(preview.template_id);
    setPreview(null);
    load();
  }

  // Group by message_type_key
  const grouped = templates.reduce((acc, t) => {
    const key = t.message_type_key || 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  // Always show all known types; append any unknown types alphabetically after
  const typeOrder = Object.keys(TYPE_META);
  const extraKeys = Object.keys(grouped).filter(k => !typeOrder.includes(k)).sort();
  const sortedGroups = [...typeOrder, ...extraKeys];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif", background: '#f0f5fb' }}>
      <AdminSidebar active="b2b-templates" />

      <main style={{ flex: 1, overflowY: 'auto', padding: '32px 36px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: NAVY }}>Email Templates</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
              {loading ? 'Loading…' : `${templates.length} template${templates.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/b2b/builder')}
            style={{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 8,
              padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + New Template
          </button>
        </div>

        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: 13, paddingTop: 60, textAlign: 'center' }}>Loading…</div>
        ) : (
          sortedGroups.map(typeKey => {
            const meta  = typeMeta(typeKey);
            const group = grouped[typeKey] || [];
            return (
              <div key={typeKey} style={{ marginBottom: 36 }}>
                {/* Group header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: meta.color, textTransform: 'uppercase',
                    letterSpacing: '0.06em' }}>{meta.label}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {group.length > 0 ? `${group.length} template${group.length !== 1 ? 's' : ''}` : 'no templates yet'}
                  </span>
                  <div style={{ flex: 1, height: 1, background: meta.color + '22' }} />
                </div>

                {/* Cards grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                  {group.map(t => (
                    <div key={t.template_id}
                      onClick={() => openPreview(t)}
                      style={{
                        background: '#fff', borderRadius: 10,
                        border: '1px solid #e2e8f0',
                        borderTop: `3px solid ${meta.color}`,
                        padding: '16px 18px', cursor: 'pointer',
                        transition: 'box-shadow 0.15s, transform 0.1s',
                        position: 'relative',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                    >
                      {/* Delete button — top right */}
                      <button
                        onClick={e => deleteTemplate(t, e)}
                        title="Delete"
                        style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none',
                          color: '#cbd5e1', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '2px 4px',
                          borderRadius: 4, opacity: 0,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0'; e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#cbd5e1'; }}
                        onFocus={e  => { e.currentTarget.style.opacity = '1'; }}
                      >✕</button>

                      <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 5, paddingRight: 20,
                        lineHeight: 1.4 }}>
                        {t.template_name}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: meta.color, textTransform: 'uppercase',
                        letterSpacing: '0.06em', marginBottom: 8 }}>
                        {meta.label}
                      </div>
                      <div style={{ fontSize: 12, color: '#374151', marginBottom: 2 }}>
                        <span style={{ fontWeight: 600 }}>Subject:</span> {t.subject}
                      </div>
                      {t.preview_text && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.5,
                          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' }}>
                          {t.preview_text}
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 10 }}>
                        Updated {fmtDate(t.updated_on_utc)}
                      </div>
                    </div>
                  ))}

                  {/* Empty state card */}
                  {group.length === 0 && (
                    <div
                      onClick={() => navigate('/admin/b2b/builder')}
                      style={{
                        background: meta.color + '06', borderRadius: 10,
                        border: `1.5px dashed ${meta.color}44`,
                        padding: '20px 18px', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', gap: 6, minHeight: 100,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = meta.color + '10'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = meta.color + '06'; }}
                    >
                      <span style={{ fontSize: 18, opacity: 0.4 }}>+</span>
                      <span style={{ fontSize: 11, color: meta.color, fontWeight: 600, opacity: 0.7 }}>
                        Build {meta.label} template
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </main>

      {/* Preview modal */}
      {(preview || loadingPreview) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => setPreview(null)}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 680,
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>

            {loadingPreview ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
            ) : preview && (
              <>
                {/* Modal header */}
                <div style={{ padding: '16px 22px', borderBottom: '1px solid #e2e8f0',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{preview.template_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                      <span style={{ fontWeight: 600 }}>Subject:</span> {preview.subject}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.06em', color: typeMeta(preview.message_type_key).color }}>
                        {typeMeta(preview.message_type_key).label}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button onClick={deleteFromPreview}
                      style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca',
                        borderRadius: 7, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                      Delete
                    </button>
                    <button onClick={() => setPreview(null)}
                      style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
                        color: '#94a3b8', lineHeight: 1, padding: '2px 4px' }}>✕</button>
                  </div>
                </div>

                {/* Rendered email body */}
                <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', fontSize: 13, lineHeight: 1.7,
                  color: '#374151' }}
                  dangerouslySetInnerHTML={{ __html: replaceVars(preview.body_html) }} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
